/**
 * One descriptor brand's override box, with a brand typeahead behind it.
 *
 * The value stored is a `watchBrands` doc id, and an id typed one character
 * wrong is indistinguishable from the problem this whole feature exists to fix —
 * so the box suggests real brands and fills their id rather than asking the
 * operator to know it. Free text is still allowed: a brand that has just been
 * created may not be in the search index yet, and refusing to store its id would
 * make the newest brands the hardest ones to reconcile.
 *
 * Matching is `useBrandSearch` — the same Algolia-backed hook every other admin
 * brand picker uses, so this box finds "Raúl PAGÈS" from "raul pages" like the
 * rest of them do.
 */

import { useState } from 'react';
import { useBrandSearch } from '../../hooks/useBrandSearch';
import type { BrandOption } from '../../services/extractorBrandOverrides';

/** Enough to choose from without turning a table row into a scroll region. */
const MAX_SUGGESTIONS = 6;

interface BrandOverrideFieldProps {
  /** The descriptor brand this override is for — used for the input's label. */
  descriptorBrandId: string;
  /** Current draft value: a `watchBrands` doc id, or '' for no override. */
  value: string;
  onChange: (next: string) => void;
  /** Brand catalogue the typeahead searches. Must be referentially stable. */
  brands: BrandOption[];
  disabled?: boolean;
}

export function BrandOverrideField({
  descriptorBrandId,
  value,
  onChange,
  brands,
  disabled = false,
}: BrandOverrideFieldProps) {
  const [open, setOpen] = useState(false);
  const { results } = useBrandSearch(brands, value);

  /*
   * Suggestions only while the box has content. A blank box means "no override",
   * and answering that with the first six brands alphabetically would be noise —
   * the operator is not browsing, they are correcting one specific slug.
   */
  const suggestions = value.trim() === '' ? [] : results.slice(0, MAX_SUGGESTIONS);
  const exactMatch = brands.some((b) => b.id === value.trim().toLowerCase());

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        disabled={disabled}
        aria-label={`Brand override for ${descriptorBrandId}`}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        /* A blur fires before the suggestion's click; mousedown below beats it. */
        onBlur={() => setOpen(false)}
        placeholder="no override"
        className="w-44 border border-gray-300 rounded px-2 py-1 text-xs focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
      />
      {open && suggestions.length > 0 && !exactMatch && (
        <ul className="absolute z-10 mt-1 w-64 max-h-56 overflow-y-auto rounded border border-gray-200 bg-white shadow-lg">
          {suggestions.map((b) => (
            <li key={b.id}>
              <button
                type="button"
                /* mousedown, not click: blur would close the list first. */
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(b.id);
                  setOpen(false);
                }}
                className="w-full text-left px-2 py-1 text-xs hover:bg-gray-50"
              >
                <span className="text-gray-900">{b.name}</span>
                <span className="ml-2 text-gray-400">{b.id}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
